import React, { useEffect, useRef, useState } from "react";
import Bugsnag from "@bugsnag/js";
import "./login.css";
import { Link } from "react-router-dom";
import { DocketAPIError, getAPIClient } from "../../apiClient";
import { useNavigate } from "react-router";
import { useForm, SubmitHandler } from "react-hook-form";
import { errorLog } from "../../utils/logger";
import { GoogleSignup, Tokens } from "../../models/Interfaces";
import { useTranslation, Trans } from "react-i18next";
import { Login as LoginInput } from "../../models/Interfaces";
import { Squirrel } from "../../components/spinner/Squirrel";
import {
  getAuth,
  connectAuthEmulator,
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailLink,
  isSignInWithEmailLink,
} from "firebase/auth";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { initializeApp } from "firebase/app";
import { firebaseConfig } from "./firebaseConfig";
import { REDIRECT_URI, STAGE, STAY_LOGGED_IN_PW, WHITELABEL_KEY } from "../../globals";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { Modal } from "../../components/modals/Modal";
import { useBranding } from "../../branding/useBranding";
import {
  appSettingsAtom,
  loadableBiometricAuthAtom,
  writeOnlyTokensAtom,
  writeOnlyUserAtom,
  darkModeAtom,
} from "../../jotai/atoms";
import { useAtom } from "jotai";
import db from "../../database/database";
import { UserResponseDto } from "@docket/consumer-app-client-typescript-axios";
import useFormFactor from "../../hooks/useFormFactor";
import { isAuth, updateCryptoKeyAndUserAndTokens } from "../../utils/auth";
import { BiometricAuth } from "@aparajita/capacitor-biometric-auth";
import { Capacitor } from "@capacitor/core";
import AppleSignIn from "./AppleSignIn";
import { vibrateDevice } from "../../utils/capacitor";
import { registerPushPermission } from "../../pushNotifications";
import { updateDevicesAtom } from "../../jotai/atomUpdater";

const app = initializeApp(firebaseConfig());
const provider = new GoogleAuthProvider();

export function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const branding = useBranding();
  const { formFactor } = useFormFactor();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isLoginWithState, setIsLoginWithState] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [openEmailPrompt, setOpenEmailPrompt] = useState(false);
  const [promptedEmail, setPromptedEmail] = useState("");
  const [_nullUser, setUser] = useAtom(writeOnlyUserAtom);
  const [_nullTokens, setTokens] = useAtom(writeOnlyTokensAtom);
  const [appSettings, setAppSettings] = useAtom(appSettingsAtom);
  const [darkMode, setDarkMode] = useAtom(darkModeAtom);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [biometricAuth] = useAtom(loadableBiometricAuthAtom);
  const [biometryAvailable, setBiometryAvailable] = useState<boolean | null>(null);

  const errorMessage = useRef(t("onboarding.login_error"));
  const hasSignInEffectRun = useRef(false);

  const onLogin: () => Promise<void> = async () => {
    // Initialize the devices atom since the push permission registration will only do so
    // once _this_ device registers.
    await updateDevicesAtom();
    await registerPushPermission();
  };

  const auth = getAuth(app);
  if (STAGE === "development" && !appSettings.firebaseEmulatorConnected) {
    connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
    setAppSettings({
      ...appSettings,
      firebaseEmulatorConnected: true,
    });
  }

  const loginWithPromptedEmail = () => {
    setOpenEmailPrompt(false);
    setLoggingIn(true);
    signInWithEmailLink(auth, promptedEmail, window.location.href)
      .then((result) => {
        result.user.getIdToken().then((idToken) => {
          getAPIClient()
            .loginWithFirebase(idToken)
            .then(async (loginResult) => {
              if (!loginResult || !loginResult.tokens) {
                // This really shouldn't happen
                const err = new Error("Did not get a log in result or token");
                Bugsnag.notify(err);
                throw err;
              }

              await setAppSettings({
                ...appSettings,
                email: loginResult.email || "",
                signedInWithFirebase: true,
              });

              // This does magic to ensure the user is actually stored locally
              await updateCryptoKeyAndUserAndTokens(loginResult, loginResult.tokens);
              await onLogin();

              // First param is the user ID, which we don't expose
              Bugsnag.setUser("", loginResult.email);
              // Check if user doesn't have info fill out
              if (
                !loginResult.dob ||
                !loginResult.legal_sex ||
                !loginResult.first_name ||
                !loginResult.last_name
              ) {
                navigate("/signup/accountinfo");
              } else if (!loginResult.verified_phone_number) {
                navigate("/signup/verifyphone");
              } else {
                navigate("/home/search");
              }
            })
            .catch((e: any) => {
              if (e instanceof DocketAPIError) {
                errorMessage.current = e.message;
              } else {
                errorLog(e);
              }
              setShowErrorModal(true);
            });
        });
      })
      .catch((error) => {
        setLoggingIn(false);
        errorMessage.current = t("onboarding.login-error");
        setShowErrorModal(true);
      })
      .finally(() => {
        window.localStorage.removeItem("emailForSignIn");
      });
  };

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      BiometricAuth.checkBiometry().then((value) => {
        setBiometryAvailable(value.isAvailable);
      });
    }
  }, []);

  useEffect(() => {
    /**
     * Prevent double execution of the useEffect in StrictMode when running the development build. Without this, we will
     * have two sign-in attempts with the same singin link or state-based login codes. One will succeed, but the other
     * will fail, which breaks the login experience.
     */
    if (hasSignInEffectRun.current) return;

    // Check for 'code' query param in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const stateParameter = urlParams.get("stateIdentifier");
    const code = urlParams.get("code");

    /* Magical links */
    const auth = getAuth(app);
    if (STAGE === "development" && !appSettings.firebaseEmulatorConnected) {
      connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
      setAppSettings({
        ...appSettings,
        firebaseEmulatorConnected: true,
      });
    }

    const hasSignInParams =
      isSignInWithEmailLink(auth, window.location.href) || (code && stateParameter);
    if (hasSignInParams) {
      hasSignInEffectRun.current = true;
    }

    if (isSignInWithEmailLink(auth, window.location.href)) {
      setLoggingIn(true);
      const email = window.localStorage.getItem("emailForSignIn");

      if (email) {
        FirebaseAuthentication.signInWithEmailLink({
          email,
          emailLink: window.location.href,
        })
          .then((result) => {
            FirebaseAuthentication.getIdToken().then((idToken) => {
              getAPIClient()
                .loginWithFirebase(idToken.token)
                .then(async (loginResult) => {
                  if (!loginResult || !loginResult.tokens) {
                    // This really shouldn't happen
                    const err = new Error("Did not get a log in result or token");
                    Bugsnag.notify(err);
                    throw err;
                  }

                  await updateCryptoKeyAndUserAndTokens(loginResult, loginResult.tokens);
                  await setAppSettings({
                    ...appSettings,
                    email: loginResult.email || "",
                  });
                  await onLogin();

                  //dispatch(setPassword(STAY_LOGGED_IN_PW));
                  //await db().setItem(Key.UserAccount, userWithPassword);
                  // First param is the user ID, which we don't expose
                  Bugsnag.setUser("", loginResult.email);
                  // Check if user doesn't have info fill out
                  if (
                    !loginResult.dob ||
                    !loginResult.legal_sex ||
                    !loginResult.first_name ||
                    !loginResult.last_name
                  ) {
                    navigate("/signup/accountinfo");
                  } else if (!loginResult.verified_phone_number) {
                    navigate("/signup/verifyphone");
                  } else {
                    navigate("/home/search");
                  }
                })
                .catch((e: any) => {
                  if (e instanceof DocketAPIError) {
                    errorMessage.current = e.message;
                  } else {
                    errorLog(e);
                  }
                  setShowErrorModal(true);
                });
            });
          })
          .catch((error) => {
            setLoggingIn(false);
            errorMessage.current = t("onboarding.login-error");
            setShowErrorModal(true);
          })
          .finally(() => {
            window.localStorage.removeItem("emailForSignIn");
          });
      } else {
        setLoggingIn(false);
        setOpenEmailPrompt(true);
      }
    }

    /* State OIDC */
    if (code && stateParameter) {
      setLoggingIn(true);
      getAPIClient()
        .loginWithAuthCode(code, stateParameter)
        .then(async (loginResult) => {
          if (!loginResult || !loginResult.tokens) {
            // This really shouldn't happen
            const err = new Error("Did not get a log in result or token");
            Bugsnag.notify(err);
            throw err;
          }

          await updateCryptoKeyAndUserAndTokens(loginResult, loginResult.tokens);
          await setAppSettings({
            ...appSettings,
            email: loginResult.email || "",
          });
          await onLogin();
          // First param is the user ID, which we don't expose
          Bugsnag.setUser("", loginResult.email);
          // Check if user doesn't have info fill out
          if (
            !loginResult.dob ||
            !loginResult.legal_sex ||
            !loginResult.first_name ||
            !loginResult.last_name
          ) {
            navigate("/signup/accountinfo");
          } else if (!loginResult.verified_phone_number) {
            navigate("/signup/verifyphone");
          } else {
            navigate("/home/search");
          }
        })
        .catch((e: any) => {
          setLoggingIn(false);
          if (e instanceof DocketAPIError) {
            errorMessage.current = e.message;
          } else {
            errorLog(e);
          }
          setShowErrorModal(true);
          setLoggingIn(false);
        });
    }
  }, []);

  useEffect(() => {
    // Check if the user is already logged in
    const checkAuth = async () => {
      // Don't race with an active sign-in flow
      const urlParams = new URLSearchParams(window.location.search);
      if (
        isSignInWithEmailLink(auth, window.location.href) ||
        (urlParams.get("code") && urlParams.get("stateIdentifier"))
      ) {
        return;
      }

      if (biometricAuth.state !== "hasData") return;
      // Want to ensure that device biometry support finished loading
      if (Capacitor.isNativePlatform() && biometryAvailable === null) return;

      const authed = await isAuth();
      if (!authed) return;

      // Do not want to navigate if biometrics is unavailable, despite setting turned on
      if (biometricAuth.data && biometryAvailable) {
        navigate("/biometrics");
      } else {
        navigate("/home/records", { replace: true });
      }
    };
    checkAuth();
  }, [biometricAuth, biometryAvailable]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LoginInput>();
  const onMagicLink: SubmitHandler<LoginInput> = async (data) => {
    try {
      setIsSubmitting(true);
      window.localStorage.setItem("emailForSignIn", data.email);
      await getAPIClient().sendSignInLink(data.email);
      setMagicLinkSent(true);
    } catch (e: any) {
      if (e && e instanceof DocketAPIError) {
        errorMessage.current = e.message;
        if ((e.cause as any)?.response?.status === 429) {
          errorMessage.current = t("onboarding.login-error-429");
        }
        errorLog(e);
      } else {
        errorLog(e);
      }
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const signInWithGoogleWeb = async () => {
    const result = await signInWithPopup(auth, provider).catch((e) => {
      // We don't want to throw if the popup is closed by the user; we should just return early...
      if (e.code === "auth/popup-closed-by-user") {
        return "popup_closed";
      }
    });
    if (result === "popup_closed") {
      return;
    }

    if (!result) {
      errorMessage.current = t("onboarding.login-error-google");
      setShowErrorModal(true);
      return;
    }

    const { user: googleuser } = result;
    const credential = GoogleAuthProvider.credentialFromResult(result);

    const googleSignup: GoogleSignup = {
      email: googleuser?.email || "",
      first_name: googleuser?.displayName?.split(" ")[0] || "",
      last_name: googleuser?.displayName?.split(" ")[1] || "",
      token: credential?.idToken || "",
      google_id: googleuser?.uid,
      offset: "", //NSTimeZone.system.secondsFromGMT() equivalent
    };

    return googleSignup;
  };

  const signInWithGoogleNative = async () => {
    const { credential, user } = await FirebaseAuthentication.signInWithGoogle();

    if (!credential?.idToken) {
      errorMessage.current = t("onboarding.login-error-google");
      setShowErrorModal(true);
      return;
    }

    const googleCredential = GoogleAuthProvider.credential(credential.idToken);

    const googleSignup: GoogleSignup = {
      email: user?.email || "",
      first_name: user?.displayName?.split(" ")[0] || "",
      last_name: user?.displayName?.split(" ")[1] || "",
      token: googleCredential?.idToken || "",
      google_id: user?.uid || "",
      offset: "", //NSTimeZone.system.secondsFromGMT() equivalent
    };

    return googleSignup;
  };

  const signInWithGoogle = async () => {
    const googleSignup = Capacitor.isNativePlatform()
      ? await signInWithGoogleNative()
      : await signInWithGoogleWeb();

    if (!googleSignup) return;

    try {
      setLoggingIn(true);
      const loginResult = await getAPIClient().loginGoogle(googleSignup);

      if (!loginResult || !loginResult.tokens?.access) {
        // This really shouldn't happen
        const err = new Error("Did not get a login result or token");
        Bugsnag.notify(err);
        throw err;
      }

      await updateCryptoKeyAndUserAndTokens(loginResult, loginResult.tokens);
      await setAppSettings({
        ...appSettings,
        email: loginResult.email || "",
      });
      await onLogin();

      // First param is the user ID, which we don't expose
      Bugsnag.setUser("", loginResult.email);
      if (
        !loginResult.dob ||
        !loginResult.legal_sex ||
        !loginResult.first_name ||
        !loginResult.last_name
      ) {
        navigate("/signup/accountinfo");
      } else if (!loginResult.verified_phone_number) {
        navigate("/signup/verifyphone");
      } else {
        navigate("/home/search");
      }
    } catch (e: any) {
      setLoggingIn(false);
      if (e instanceof DocketAPIError) {
        errorMessage.current = e.message;
      } else {
        errorLog(e);
      }
      setShowErrorModal(true);
    }
  };

  const loginWithState = async (state: string) => {
    await getAPIClient().initiateOidcAuth(state);
  };

  const handleAppleSignInError = (error: string) => {
    errorMessage.current = error;
    setShowErrorModal(true);
  };
  const toggleDarkMode = async () => {
    if (!darkMode) await vibrateDevice();
    setDarkMode(!darkMode);
  };

  return (
    <div className="flex flex-col h-full overflow-x-hidden overflow-y-scroll">
      <div className="flex-1 flex items-center justify-center -mt-[env(safe-area-inset-top)] override-bg-use-gradient">
        <div className="relative">
          <img
            className={`absolute top-0 h-full w-auto max-w-none -translate-x-1/3 translate-y-1/6 z-10 pointer-events-none ${
              magicLinkSent ? "" : ""
            }`}
            src="/assets/images/signin-viruses-left.png"
          ></img>
          <img
            className={`absolute right-0 top-0 h-full w-auto max-w-none translate-x-1/3 translate-y-1/6 z-10 pointer-events-none ${
              magicLinkSent ? "" : ""
            }`}
            src="/assets/images/signin-viruses-right.png"
          ></img>
          <div className="card bg-base-100 md:w-xl max-md:max-w-screen overflow-hidden relative max-md:w-xs">
            {magicLinkSent && (
              <div className="absolute top-2 left-2">
                <Link to="/" className="btn" onClick={() => setMagicLinkSent(false)}>
                  {t("onboarding.signup-back-button")}
                </Link>
              </div>
            )}
            {showErrorModal && (
              <ErrorModal onCloseModal={() => setShowErrorModal(false)}>
                <p>{errorMessage.current}</p>
              </ErrorModal>
            )}
            <div className="flex justify-center pt-8">
              <img
                src={branding.logo}
                alt="Docket logo"
                className="max-w-48"
                data-testid="docket-logo"
              />
            </div>
            {isLoginWithState ? (
              <div className="card-body">
                <a
                  onClick={() => setIsLoginWithState(false)}
                  role="button"
                  className="absolute top-2 left-2 btn"
                >
                  {t("generic.back")}
                </a>
                <div className="text-center">{t("onboarding.state-disclaimer")}</div>
                <div className="flex flex-col items-center gap-2">
                  <button
                    data-testid="alaska-login-button"
                    onClick={() => loginWithState("ak")}
                    type="button"
                    className="btn btn-lg w-64 max-w-screen bg-white py-2"
                  >
                    <img src={"assets/images/myalaska.png"} className="h-full" />
                  </button>
                  <button
                    data-testid="utah-login-button"
                    onClick={() => loginWithState("ut")}
                    type="button"
                    className="btn btn-lg w-64 max-w-screen bg-white py-2"
                  >
                    <img src={"assets/images/utahid-logo.svg"} className="h-full" />
                  </button>
                  <button
                    data-testid="missouri-login-button"
                    onClick={() => loginWithState("mo")}
                    type="button"
                    className="btn btn-lg w-64 max-w-screen bg-white py-2"
                  >
                    <img src={"assets/images/mymogov-login.png"} className="h-full" />
                  </button>
                </div>
                <div className="text-xs max-md:hidden">
                  <b>{t("generic.warning")}:</b> {t("onboarding.state-persist-message")}
                </div>
                <div className="text-xs max-md:hidden">
                  <Trans i18nKey="onboarding.inactivity-message" />
                </div>
              </div>
            ) : (
              <>
                {magicLinkSent && (
                  <div className="card-body items-center text-center">
                    <div data-testid="sent-signin-link" className="card-title">
                      {t("onboarding.sent-signin-link")}
                    </div>
                    <p className="text-primary font-bold text-xl break-all">
                      {window.localStorage.getItem("emailForSignIn")}
                    </p>
                    <p className="font-medium max-md:hidden">{t("onboarding.click-signin-link")}</p>
                    <p className="md:hidden">
                      Tap the sign-in link <i className="font-semibold">using this device</i> to
                      confirm your email address.
                    </p>
                    <img
                      src={"assets/images/Icon_Info.png"}
                      className="dark:hidden size-[70px] mx-auto"
                    />
                    <img
                      src={"assets/images/Icon_Info_White.png"}
                      className="not-dark:hidden size-[70px] mx-auto"
                    />
                    <p className="font-medium md:hidden text-md">
                      {t("onboarding.enable-notification-reminders")}
                    </p>
                  </div>
                )}
                {loggingIn && (
                  <div className="is-fullWidth my-6">
                    <Squirrel />
                  </div>
                )}
                {!magicLinkSent && !loggingIn && (
                  <>
                    <form
                      className="card-body items-center md:px-16 pb-4"
                      onSubmit={handleSubmit(onMagicLink)}
                    >
                      <fieldset className="fieldset relative w-full flex justify-center">
                        <input
                          data-testid="email-input"
                          type="email"
                          className="input input-lg input-info max-md:w-full md:min-w-96"
                          aria-label={t("onboarding.email-address")}
                          placeholder={t("onboarding.email-address")}
                          defaultValue=""
                          {...register("email", {
                            required: t("onboarding.email-required"),
                          })}
                        />
                        {errors["email"] && (
                          <div className="validation-error absolute -top-5 left-4">
                            {<span>{errors["email"] && errors["email"].message}</span>}
                          </div>
                        )}
                      </fieldset>
                      <p className="max-md:hidden text-xs font-normal text-center">
                        <Trans i18nKey="onboarding.inactivity-message" />
                      </p>
                      <button
                        data-testid="login-btn"
                        className="btn btn-lg btn-primary max-md:w-full md:min-w-64"
                        type="submit"
                        disabled={isSubmitting || errors.email !== undefined}
                      >
                        {t("onboarding.signup_or_login")}
                      </button>
                    </form>
                    <div className="card-body bg-info jersey:bg-secondary/20 dark:bg-secondary pt-4">
                      <p className="my-auto uppercase font-bold text-center">
                        {formFactor === "desktop" ? t("onboarding.or-login-with") : "OR"}
                      </p>
                      <div className="flex flex-wrap max-md:flex-col justify-center gap-2">
                        <button
                          data-testid="Sign-in-Google"
                          onClick={signInWithGoogle}
                          type="button"
                          className="btn btn-lg bg-white text-black"
                        >
                          <svg
                            aria-label="Google logo"
                            width="24"
                            height="24"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                          >
                            <g>
                              <path d="m0 0H512V512H0" fill="#fff"></path>
                              <path
                                fill="#34a853"
                                d="M153 292c30 82 118 95 171 60h62v48A192 192 0 0190 341"
                              ></path>
                              <path
                                fill="#4285f4"
                                d="m386 400a140 175 0 0053-179H260v74h102q-7 37-38 57"
                              ></path>
                              <path
                                fill="#fbbc02"
                                d="m90 341a208 200 0 010-171l63 49q-12 37 0 73"
                              ></path>
                              <path
                                fill="#ea4335"
                                d="m153 219c22-69 116-109 179-50l55-54c-78-75-230-72-297 55"
                              ></path>
                            </g>
                          </svg>
                          <span className="">Google</span>
                        </button>
                        <AppleSignIn
                          onLogin={onLogin}
                          onError={handleAppleSignInError}
                          setLoggingIn={setLoggingIn}
                        />
                        {!Capacitor.isNativePlatform() && (
                          <button
                            data-testid="state-issued-button"
                            onClick={() => setIsLoginWithState(true)}
                            type="button"
                            className="btn btn-lg"
                          >
                            {t("onboarding.state-login-option")}
                          </button>
                        )}
                      </div>
                      <div className="text-xs max-md:hidden text-center">
                        {t("onboarding.allow-popups")}
                      </div>
                      {WHITELABEL_KEY !== "nj" && (
                        <label className="flex items-center justify-center gap-4  cursor-pointer py-2">
                          <span className="text-base font-semibold">Dark Mode</span>
                          <input
                            type="checkbox"
                            className="toggle toggle-lg toggle-primary"
                            checked={darkMode}
                            onChange={toggleDarkMode}
                            data-testid="drawer-dark-mode-toggle"
                          />
                        </label>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Modal
        id="email-prompt"
        open={openEmailPrompt}
        onCloseModal={() => setOpenEmailPrompt(false)}
        hasClose={true}
        title={t("onboarding.enter_email")}
        onConfirm={loginWithPromptedEmail}
        confirm={t("onboarding.login")}
      >
        <div className="field mb-4 mt-4">
          <p className="mb-4">{t("onboarding.login-reenter-email")}</p>
          <fieldset className="fieldset p-0">
            <legend className="fieldset-legend pt-0 text-[16px] font-[700] text-black">
              Email
            </legend>
            <input
              id="promptedEmail"
              className="input w-full text-black"
              placeholder={t("onboarding.email")}
              value={promptedEmail}
              onChange={(e) => setPromptedEmail(e.target.value)}
            />
          </fieldset>
        </div>
      </Modal>
      {WHITELABEL_KEY === "nj" && (
        <footer className="footer pb-2 max-md:hidden">
          <div className="footer-content mx-auto">
            <div className="flex justify-center items-center">
              <a
                className="link mr-2 ml-2 py-1"
                href="https://www.nj.gov/health/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("onboarding.nj_link")}
              </a>
              <span className="py-1">|</span>
              <a
                className="link mr-2 ml-2 py-1"
                href="https://dockethealth.com/terms_of_use"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("onboarding.terms_link")}
              </a>
              <span className="py-1">|</span>
              <a
                className="link mr-2 ml-2 py-1 "
                href="https://dockethealth.com/privacy_policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("onboarding.privacy_link")}
              </a>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

export default Login;
